/**
 * iter 53 / 63 — UNIFIED LOCATION MANAGER DRAWER.
 *
 * Canonical drawer for Location create + edit anywhere in the app:
 *   • OrgAdmin → office branches  (locationKind="office")
 *   • New Job  → service-target addresses (locationKind="service")
 *
 * Both kinds share the same fields (name, address, contact, Google Places
 * autocomplete, lat/lng, phone). The `locationKind` prop chooses the
 * backend endpoint and the surrounding helper text.
 *
 * iter 63 — now uses the shared `useDrawerForm` lifecycle hook + sticky
 * `<DrawerFooter>` for dirty-guarded close and a uniform action bar.
 *
 * Endpoints:
 *   office:  /api/org-admin/{orgId}/locations         (CRUD)
 *   service: /api/locations                           (CRUD)
 */
import { useState } from "react";
import { toast } from "sonner";
import { Star } from "@phosphor-icons/react";
import { api, formatErr } from "../../lib/api";
import { Drawer, Field } from "../UI";
import GooglePlacesAutocomplete from "../GooglePlacesAutocomplete";
import {
  ErrorBanner, DrawerFooter, useDrawerForm,
} from "./_managerShared";

const EMPTY = {
  name: "", contact_name: "", phone: "",
  address1: "", address2: "", city: "", state: "", postal_code: "",
  latitude: null, longitude: null,
  is_main_location: false,
  notes: "",  // iter 71.1 — driver-facing notes shown on jobs/routes.
  // iter 71.2 — service-kind only: business taxonomy for routing/filtering.
  location_type_id: "", location_priority_id: "",
};

const hydrate = (rec) => ({
  name: rec.name || "",
  contact_name: rec.contact_name || "",
  phone: rec.phone || "",
  address1: rec.address1 || "",
  address2: rec.address2 || "",
  city: rec.city || "",
  state: rec.state || "",
  postal_code: rec.postal_code || "",
  latitude: rec.latitude,
  longitude: rec.longitude,
  is_main_location: !!rec.is_main_location,
  notes: rec.notes || "",
  location_type_id: rec.location_type_id || "",
  location_priority_id: rec.location_priority_id || "",
});

export default function LocationManagerDrawer({
  open, onClose, onSuccess, mode = "create", initialData = null,
  orgId, orgName, locationKind = "office", isOnlyLocation = false,
  // iter 71.2 — Type + Priority dropdowns are surfaced for service-kind
  // locations (used by the /locations page). Office-kind drawers leave
  // these empty so the fields stay hidden.
  types = [], priorities = [],
}) {
  const isEdit = mode === "edit";
  const f = useDrawerForm({ open, isEdit, initialData, emptyForm: EMPTY, hydrate });
  const { form, setForm, setField, busy, setBusy, err, setErr, requestClose } = f;
  // Search text is intentionally OUTSIDE the form state — it's an
  // input-only widget that drives autocomplete picks; it doesn't get
  // persisted and shouldn't trip the dirty flag.
  const [searchText, setSearchText] = useState("");

  const onPlace = (parsed) => {
    setForm((cur) => ({
      ...cur,
      address1: parsed.address1 || cur.address1,
      city: parsed.city || cur.city,
      state: parsed.state || cur.state,
      postal_code: parsed.postal_code || cur.postal_code,
      latitude: parsed.latitude ?? cur.latitude,
      longitude: parsed.longitude ?? cur.longitude,
    }));
    setSearchText(parsed.formatted_address || "");
  };

  const endpoint = (idSuffix = "") => {
    if (locationKind === "office") {
      return `/org-admin/${orgId}/locations${idSuffix}`;
    }
    return `/locations${idSuffix}`;
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        name: form.name,
        contact_name: form.contact_name || null,
        phone: form.phone || null,
        address1: form.address1 || null,
        address2: form.address2 || null,
        city: form.city || null,
        state: form.state || null,
        postal_code: form.postal_code || null,
        latitude: form.latitude,
        longitude: form.longitude,
        notes: form.notes || null,  // iter 71.1
      };
      // iter 71.2 — service-kind only fields. Skip them on office drawers so
      // the backend office endpoint (which doesn't accept them) stays clean.
      if (locationKind === "service") {
        payload.location_type_id = form.location_type_id || null;
        payload.location_priority_id = form.location_priority_id || null;
      }
      let saved;
      if (isEdit) {
        if (locationKind === "office") payload.is_main_location = form.is_main_location;
        const r = await api.patch(endpoint(`/${initialData.id}`), payload);
        saved = r.data;
      } else {
        const r = await api.post(endpoint(""), payload);
        saved = r.data;
      }
      toast.success(isEdit ? `Location "${form.name}" updated.` : `Location "${form.name}" added.`);
      onSuccess?.(saved);
      onClose?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!isEdit || !initialData) return;
    if (!confirm(`Delete location "${initialData.name}"?`)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(endpoint(`/${initialData.id}`));
      toast.success(`Location "${initialData.name}" deleted.`);
      onSuccess?.(null);
      onClose?.();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={() => requestClose(onClose)}
      title={isEdit ? "Edit location" : "Add location"}
      size="narrow"
      testid="location-manager-drawer"
      footer={
        <DrawerFooter
          testid="location-manager-footer"
          onCancel={() => requestClose(onClose)}
          onSave={save}
          mode={mode}
          busy={busy}
          saveLabel={isEdit ? "Update location" : "Create location"}
          saveDisabled={!form.name.trim()}
          left={isEdit && !isOnlyLocation && (
            <button
              type="button"
              className="btn btn-ghost text-destructive"
              onClick={remove}
              disabled={busy}
              data-testid="location-manager-delete-btn"
            >
              DELETE
            </button>
          )}
        />
      }
    >
      <ErrorBanner err={err} testid="location-manager-error" />

      {!isEdit && locationKind === "office" && (
        <div className="surface p-2 mb-3 text-[11px] flex items-center gap-2 bg-blue-50 dark:bg-blue-950/40 border-blue-300">
          <Star size={12} weight="fill" className="text-primary" />
          <span>The first location created is automatically the Main Location.</span>
        </div>
      )}

      <Field label="LOCATION NAME">
        <input className="input" autoFocus value={form.name}
          onChange={(e) => setField("name", e.target.value)}
          placeholder={locationKind === "office" ? "e.g. Chicago HQ" : "e.g. Defendant Residence"}
          data-testid="location-manager-name-input" />
      </Field>

      <Field label="CONTACT">
        <input className="input" value={form.contact_name}
          onChange={(e) => setField("contact_name", e.target.value)}
          placeholder="On-site contact name"
          data-testid="location-manager-contact-input" />
      </Field>

      <Field label="SEARCH ADDRESS (GOOGLE PLACES)">
        <GooglePlacesAutocomplete
          value={searchText}
          onChange={setSearchText}
          onPlaceSelected={onPlace}
          placeholder="Start typing — pick a suggestion to auto-fill the fields below"
          testid="location-manager-places-input"
        />
        <div className="text-[10px] text-muted-foreground mt-1">
          Picking a Google suggestion auto-fills Address Line 1, City, State, Zip and lat/lng.
          You can also type each field manually if the address isn&apos;t found.
        </div>
      </Field>

      <Field label="ADDRESS LINE 1">
        <input className="input" value={form.address1}
          onChange={(e) => setField("address1", e.target.value)}
          placeholder="123 Main St"
          data-testid="location-manager-address1-input" />
      </Field>

      <Field label="ADDRESS LINE 2">
        <input className="input" value={form.address2}
          onChange={(e) => setField("address2", e.target.value)}
          placeholder="Suite, floor, etc." />
      </Field>

      <div className="grid grid-cols-[2fr_1fr_1fr] gap-3">
        <Field label="CITY">
          <input className="input" value={form.city}
            onChange={(e) => setField("city", e.target.value)}
            data-testid="location-manager-city-input" />
        </Field>
        <Field label="STATE">
          <input className="input" value={form.state}
            onChange={(e) => setField("state", e.target.value)}
            data-testid="location-manager-state-input" />
        </Field>
        <Field label="ZIP">
          <input className="input" value={form.postal_code}
            onChange={(e) => setField("postal_code", e.target.value)}
            data-testid="location-manager-zip-input" />
        </Field>
      </div>

      <Field label="PHONE">
        <input className="input" value={form.phone}
          onChange={(e) => setField("phone", e.target.value)} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="LATITUDE">
          <input className="input mono text-xs" value={form.latitude ?? ""}
            onChange={(e) => setField("latitude", e.target.value ? parseFloat(e.target.value) : null)}
            placeholder="auto-filled by Places" />
        </Field>
        <Field label="LONGITUDE">
          <input className="input mono text-xs" value={form.longitude ?? ""}
            onChange={(e) => setField("longitude", e.target.value ? parseFloat(e.target.value) : null)}
            placeholder="auto-filled by Places" />
        </Field>
      </div>

      {/* iter 71.2 — Service-kind taxonomy (type + priority). Office drawers
          pass empty arrays so this block stays hidden. */}
      {locationKind === "service" && (types.length > 0 || priorities.length > 0) && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="TYPE">
            <select
              className="input"
              value={form.location_type_id}
              onChange={(e) => setField("location_type_id", e.target.value)}
              data-testid="location-manager-type-select"
            >
              <option value="">—</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </Field>
          <Field label="PRIORITY">
            <select
              className="input"
              value={form.location_priority_id}
              onChange={(e) => setField("location_priority_id", e.target.value)}
              data-testid="location-manager-priority-select"
            >
              <option value="">—</option>
              {priorities.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>
        </div>
      )}

      {/* iter 71.1 — Driver-facing notes. Last field so it stays visually at the bottom. */}
      <Field label="NOTES (VISIBLE TO DRIVERS)">
        <textarea
          className="input min-h-[88px] resize-y"
          value={form.notes}
          onChange={(e) => setField("notes", e.target.value)}
          placeholder={'e.g. "Gray house, second from the end on the right" — anything that helps drivers find or identify the place.'}
          data-testid="location-manager-notes-input"
        />
        <div className="text-[10px] text-muted-foreground mt-1">
          Free text. Shown to drivers on the job detail + route sheet — keep it short and specific.
        </div>
      </Field>

      {isEdit && locationKind === "office" && (
        <label className="flex items-center gap-2 mt-2 cursor-pointer">
          <input type="checkbox"
            checked={form.is_main_location}
            onChange={(e) => setField("is_main_location", e.target.checked)}
            data-testid="location-manager-main-toggle" />
          <Star size={12} weight="fill" className="text-primary" />
          <span className="text-sm">Mark as Main Location</span>
        </label>
      )}
    </Drawer>
  );
}
